import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const hasVerifiedRef = useRef(false);
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    const verifyEmail = async () => {
      // If no token, account was auto-verified — redirect to login
      if (!token) {
        setStatus('success');
        toast.success('Your account is ready. You can log in now.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      try {
        await api.get(`/auth/verify-email?token=${token}`);
        setStatus('success');
        toast.success('Account verified! You can now log in.');
        setTimeout(() => navigate('/login'), 2000);
      } catch (error) {
        // Token may be expired or already used — account may still be active
        // Try to redirect gracefully rather than showing a hard error
        const detail = error.response?.data?.detail || '';
        if (detail.toLowerCase().includes('already verified') || detail.toLowerCase().includes('already used')) {
          setStatus('success');
          toast.success('Account already active. You can log in.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          // For any other error, still redirect to login with a soft message
          // since auto-verification means the account likely already works
          setStatus('redirect');
          setTimeout(() => navigate('/login'), 3000);
        }
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-gray-900 border border-gray-800 p-12 rounded-sm">
          {(status === 'verifying') && (
            <div>
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500 mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-2">One moment...</h2>
              <p className="text-gray-500 font-mono text-sm">Confirming your account</p>
            </div>
          )}

          {status === 'success' && (
            <div>
              <div className="text-green-500 text-6xl mb-6">✓</div>
              <h2 className="text-2xl font-bold text-white mb-2">You're all set!</h2>
              <p className="text-gray-500 font-mono text-sm mb-4">
                Your account is active and ready to use.
              </p>
              <p className="text-gray-600 text-xs">Redirecting to login...</p>
            </div>
          )}

          {status === 'redirect' && (
            <div>
              <div className="text-yellow-500 text-6xl mb-6">→</div>
              <h2 className="text-2xl font-bold text-white mb-2">Redirecting...</h2>
              <p className="text-gray-500 font-mono text-sm mb-4">
                Taking you to the login page. If you already registered, you can log in now.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="bg-green-500 hover:bg-green-600 text-black font-bold py-2 px-6 rounded-sm uppercase tracking-wider transition-colors"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
